import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {HotelSearchRestrictions, OrganizationParameter, Restrictions, TourvisioRentingAgencyRestrictions} from '../../../utils/objects/organization-parameter';
import { ApiService } from '../../../utils/services/api.service';
import { ActivatedRoute } from '@angular/router';
import {CURRENCIES, RENTING_OFFER_TYPES, RESTRICTION_PARAMETERS} from '../../../utils/constants/constants';
import { Observable } from 'rxjs/Observable';
import { NotificationsService } from 'angular2-notifications';

@Component({
  selector: 'app-organization-parameter',
  templateUrl: './organization-parameter.component.html',
  styleUrls: ['./organization-parameter.component.css']
})
export class OrganizationParameterComponent implements OnInit {
  public organizationParameterValue: OrganizationParameterValue;
  public parameterForm: FormGroup;
  public currencies = CURRENCIES;
  public rentingOfferTypes: string[] = RENTING_OFFER_TYPES;
  public organizationID;
  public isAgency;
  public costCenterForm: FormGroup;
  public hotelSearchRestrictionsForm: FormGroup;
  public searchPriorityRestrictionForm: FormGroup;
  public rentingRestrictedOfferTypesForm: FormGroup;
  public additionalFeatures: FormGroup;
  public costCenters = null;
  public selectedCostCenter = null;
  public showLoading = false;
  public searchPriorityRestriction = null;
  public tourvisioRentingAgency = null;
  public hotelSearchRestrictions = null;
  public restrictionKeys: string[] = [
    'secondBillingAddress',
    'integrationReportPurchaseRow',
    'mailOrder'
  ];
  public allRestrictions: OrganizationParameter[] = [];

  constructor(
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private notificationsService: NotificationsService
  ) { }

  ngOnInit() {
    this.organizationID = this.activatedRoute.snapshot.parent.params.id;
    this.isAgency = this.activatedRoute.snapshot.data.isAgency;
    this.organizationParameterValue = new OrganizationParameterValue();
    this.fetchCostCenters();
    this.parameterForm = new FormGroup({
      currency: new FormControl(null, Validators.required),
      internationalTimeWindow: new FormControl(null, Validators.required),
      domesticTimeWindow: new FormControl(null, Validators.required),
      internationalMaxStops: new FormControl(null, Validators.required),
      domesticMaxStops: new FormControl(null, Validators.required),
      justificationReasonCode: new FormControl(null, Validators.required),
      travelDeclineReasonCode: new FormControl(null, Validators.required),
      policyReasonCode: new FormControl(null, Validators.required),
      app: new FormControl(null, Validators.required),
    });
    this.costCenterForm = new FormGroup({
      'Currency': new FormControl(null),
      'CCName': new FormControl(null),
      'CCCode': new FormControl(null),
    });
    this.organizationParameterValue = this.activatedRoute.snapshot.data.organizationParameters;

    this.fetchRestrictions();
    
    this.parameterForm.controls.currency.patchValue(
      this.organizationParameterValue.currency
        ? this.organizationParameterValue.currency.value
        : null
    );
    this.parameterForm.controls.internationalTimeWindow.patchValue(
      this.organizationParameterValue.internationalTimeWindow
        ? this.organizationParameterValue.internationalTimeWindow.value
        : null
    );
    this.parameterForm.controls.internationalMaxStops.patchValue(
      this.organizationParameterValue.internationalMaxStops
        ? this.organizationParameterValue.internationalMaxStops.value
        : null
    );
    this.parameterForm.controls.domesticTimeWindow.patchValue(
      this.organizationParameterValue.domesticTimeWindow
        ? this.organizationParameterValue.domesticTimeWindow.value
        : null
    );
    this.parameterForm.controls.domesticMaxStops.patchValue(
      this.organizationParameterValue.domesticMaxStops
        ? this.organizationParameterValue.domesticMaxStops.value
        : null
    );
    this.hotelSearchRestrictionsForm = new FormGroup({
      currency: new FormControl(this.getHotelSearchRestrictionsCurrency(), Validators.required),
      country: new FormControl(this.getHotelSearchRestrictionsCountries(), Validators.required),
      day: new FormControl(this.getHotelSearchRestrictionsDay(), Validators.required)
    });

    this.searchPriorityRestrictionForm = new FormGroup({
      maxSelectedHotels: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(100)]),
    });

    this.rentingRestrictedOfferTypesForm = new FormGroup({
      fullCredit: new FormControl(false),
      partialCredit: new FormControl(false)
    });

    this.additionalFeatures = new FormGroup({
      secondBillingAddress: new FormControl(false),
      integrationReportPurchaseRow: new FormControl(false),
      mailOrder: new FormControl(false)
    });

    this.patchRestrictionValues();
  }

  patchRestrictionValues() {

    this.restrictionKeys.forEach((key) => {
      const restriction = this.allRestrictions[key];
      const control = this.additionalFeatures.controls[key];
  
      if (restriction && restriction.value === RESTRICTION_PARAMETERS.values[key].active) {
          control.patchValue(true);
      } else {
          control.patchValue(false);
      }
    });

    this.searchPriorityRestrictionForm.controls.maxSelectedHotels.patchValue(
      this.searchPriorityRestriction 
        ? parseInt(this.searchPriorityRestriction.value, 10)
        : null
    );

    const parsedRestrictedOfferTypes = this.tourvisioRentingAgency && this.tourvisioRentingAgency.restrictedOfferTypes 
      ? this.tourvisioRentingAgency.restrictedOfferTypes.value.split("|") 
      : [];
    
    this.rentingRestrictedOfferTypesForm.controls.fullCredit.patchValue(parsedRestrictedOfferTypes.includes("FullCredit"));
    this.rentingRestrictedOfferTypesForm.controls.partialCredit.patchValue(parsedRestrictedOfferTypes.includes("PartialCredit"));
   
  }

  fetchRestrictions() {
    
    if (this.organizationParameterValue.restrictions) {
      Object.entries(this.organizationParameterValue.restrictions).forEach(([key, restriction]) => {
        if (this.restrictionKeys.includes(key)) {
          this.allRestrictions[key] = restriction;
        }
      });

      this.restrictionKeys.forEach(key => {
        if (typeof this.allRestrictions[key] === 'undefined') {
          this.allRestrictions[key] = null;
        }
      });
    }

    this.hotelSearchRestrictions = this.organizationParameterValue.restrictions && this.organizationParameterValue.restrictions.hotelSearch 
      ? this.organizationParameterValue.restrictions.hotelSearch : null;

    this.searchPriorityRestriction = this.organizationParameterValue.restrictions && this.organizationParameterValue.restrictions.searchPriority 
      ? this.organizationParameterValue.restrictions.searchPriority : null;

    this.tourvisioRentingAgency = this.organizationParameterValue.restrictions && this.organizationParameterValue.restrictions.tourvisioRentingAgency 
      ? this.organizationParameterValue.restrictions.tourvisioRentingAgency : null;

  }

  fetchCostCenters() {
    this.showLoading = true;
    this.apiService.getCostCenters(this.organizationID).subscribe(res => {
      this.showLoading = false;
      this.costCenters = res.data.rows;
    }, err => {
      this.costCenters = [];
      this.showLoading = false;
    })
  }
  deleteCostCenter(item, itemIndex) {
    this.apiService.deleteCostCenter(this.organizationID, item.id).subscribe(res => {
      this.notificationsService.success('Success', 'Operation is successful!');
      this.costCenters.splice(itemIndex, 1);
    }, err => {
      if (err.error.code == 300004030)
        this.notificationsService.warn('Warning', 'You cannot delete last cost center!');
      else
        this.notificationsService.error('Error', 'Operation failed!');
    })
  }

  editCostCenter(item, itemIndex) {
    this.selectedCostCenter = JSON.parse(JSON.stringify(item));
    this.costCenterForm.controls['CCCode'].patchValue(item.code);
    this.costCenterForm.controls['CCName'].patchValue(item.name);
  }
  saveCostCenter() {
    if (this.costCenterForm.valid) {
      if (!this.selectedCostCenter) {
        let data = {
          "code": this.costCenterForm.value.CCCode,
          "name": this.costCenterForm.value.CCName
        }
        this.apiService.createCostCenter(this.organizationID, data).subscribe(res => {
          this.notificationsService.success('Success', 'Operation is successful!');
          this.costCenters.push(res.data);
          this.costCenterForm.reset();
        }, err => {
          this.notificationsService.error('Error', 'Operation failed!');
        })
      } else {
        let data = {
          "code": this.costCenterForm.value.CCCode,
          "name": this.costCenterForm.value.CCName
        }
        this.apiService.updateCostCenter(this.organizationID, this.selectedCostCenter.id, data).subscribe(res => {
          this.notificationsService.success('Success', 'Update operation is successful!');
          let item = this.costCenters.find(item => item.id == this.selectedCostCenter.id);
          item.code = data.code;
          item.name = data.name;
          this.costCenterForm.reset();
        }, err => {
          this.notificationsService.error('Error', 'Operation failed!');
        })
      }
    } else {
      this.notificationsService.warn('Warning', 'Please fill empty fields!');
    }
  }

  activateReasonCode(id, parameter) {
    parameter.status = 'Active'
    this.apiService.updateOrganizationParameter(this.organizationID, id, parameter).subscribe(res => {
      parameter = res.data;
      this.notificationsService.success('Success', 'Reason code activated successfully!');
    }, err => {
      this.notificationsService.error('Error', 'Operation failed!');
    })
  }
  saveLLF() {
    if (!this.parameterForm.controls.domesticMaxStops.valid ||
      !this.parameterForm.controls.domesticTimeWindow.valid ||
      !this.parameterForm.controls.internationalTimeWindow.valid ||
      !this.parameterForm.controls.internationalMaxStops.valid) {
      this.notificationsService.warn('Warning', 'Please fill empty fields!')
      return;
    }

    const requests: Array<Observable<any>> = [];

    requests.push(
      this.createRequest(
        'LowestLogicalFare',
        this.organizationParameterValue.domesticMaxStops,
        'Domestic',
        'maxStops',
        this.parameterForm.controls.domesticMaxStops.value
      )
    );
    requests.push(
      this.createRequest(
        'LowestLogicalFare',
        this.organizationParameterValue.domesticTimeWindow,
        'Domestic',
        'timeWindow',
        this.parameterForm.controls.domesticTimeWindow.value
      )
    );
    requests.push(
      this.createRequest(
        'LowestLogicalFare',
        this.organizationParameterValue.internationalMaxStops,
        'International',
        'maxStops',
        this.parameterForm.controls.internationalMaxStops.value
      )
    );
    requests.push(
      this.createRequest(
        'LowestLogicalFare',
        this.organizationParameterValue.internationalTimeWindow,
        'International',
        'timeWindow',
        this.parameterForm.controls.internationalTimeWindow.value
      )
    );
    Observable.forkJoin(requests).subscribe(res => {
      this.notificationsService.success('Success', 'Save operation is successful!');
      res.forEach(item => {
        item = item.data;
        if (item.key == 'maxStops' && item.context == 'Domestic') {
          this.organizationParameterValue.domesticMaxStops = item;
        }
        if (item.key == 'maxStops' && item.context == 'International') {
          this.organizationParameterValue.internationalMaxStops = item;
        }
        if (item.key == 'timeWindow' && item.context == 'timeWindow') {
          this.organizationParameterValue.domesticTimeWindow = item;
        }
        if (item.key == 'timeWindow' && item.context == 'International') {
          this.organizationParameterValue.internationalTimeWindow = item;
        }
      })
    }, err => {
      this.notificationsService.error('Failed', 'Save operation is failed!')
    });
  }
  saveReasonCodes() {

    const requests: Array<Observable<any>> = [];
    if (this.parameterForm.controls.policyReasonCode.value) {
      requests.push(
        this.createRequest(
          'PolicyReasonCode',
          null,
          'PolicyReasonCode',
          this.parameterForm.controls.policyReasonCode.value,
          this.parameterForm.controls.policyReasonCode.value
        )
      );
    }
    if (this.parameterForm.controls.travelDeclineReasonCode.value) {
      requests.push(
        this.createRequest(
          'TravelDeclineReasonCode',
          null,
          'TravelDeclineReasonCode',
          this.parameterForm.controls.travelDeclineReasonCode.value,
          this.parameterForm.controls.travelDeclineReasonCode.value
        )
      );
    }
    if (this.parameterForm.controls.justificationReasonCode.value) {
      requests.push(
        this.createRequest(
          'JustificationReasonCode',
          null,
          'JustificationReasonCode',
          this.parameterForm.controls.justificationReasonCode.value,
          this.parameterForm.controls.justificationReasonCode.value
        )
      );
    }

    Observable.forkJoin(requests).subscribe(res => {
      this.notificationsService.success('Success', 'Save operation is successful!')
      if (this.parameterForm.controls.policyReasonCode.value)
        this.organizationParameterValue.policyReasonCodes.push(res.find(item => item.data.context == 'PolicyReasonCode').data);
      if (this.parameterForm.controls.travelDeclineReasonCode.value)
        this.organizationParameterValue.travelDeclineReasonCodes.push(res.find(item => item.data.context == 'TravelDeclineReasonCode').data);
      if (this.parameterForm.controls.justificationReasonCode.value)
        this.organizationParameterValue.justificationReasonCodes.push(res.find(item => item.data.context == 'JustificationReasonCode').data);
    }, err => {
      if (err.error.code == 400606001) {
        this.notificationsService.warn('Failed', 'You already added these reason codes!')
      } else {
        this.notificationsService.error('Failed', 'Save operation is failed!')
      }
    });
  }

  saveCurrency() {
    if (this.parameterForm.controls.currency.valid) {
      this.createRequest(
        'Currency',
        this.organizationParameterValue.currency,
        'currency',
        'currency',
        this.parameterForm.controls.currency.value
      ).subscribe(res => {
        this.notificationsService.success('Success', 'Save operation is successful!')
      }, err => {
        this.notificationsService.error('Failed', 'Save operation is failed!')
      });
    } else {
      this.notificationsService.warn('Warning', 'Please select currency!');
    }

  }

  deleteJustificationReasonCode(parameterId, parameterIndex) {
    this.apiService.deleteOrganizationParameters(this.organizationID, parameterId).subscribe(res => {
      this.notificationsService.success('Success', 'Reason code inactivated successfully!')
      this.organizationParameterValue.justificationReasonCodes[parameterIndex]['status'] = 'Inactive';

    }, err => {
      this.notificationsService.error('Failed', 'Inactive operation is failed!')
    });
  }

  deletePolicyReasonCode(parameterId, parameterIndex) {
    this.apiService.deleteOrganizationParameters(this.organizationID, parameterId).subscribe(res => {
      this.notificationsService.success('Success', 'Reason code inactivated successfully!');
      this.organizationParameterValue.policyReasonCodes[parameterIndex]['status'] = 'Inactive';

    }, err => {
      this.notificationsService.error('Failed', 'Inactive operation is failed!')
    });
  }

  deleteTravelDeclineReasonCode(parameterId, parameterIndex) {
    this.apiService.deleteOrganizationParameters(this.organizationID, parameterId).subscribe(res => {
      this.organizationParameterValue.travelDeclineReasonCodes[parameterIndex]['status'] = 'Inactive';
      this.notificationsService.success('Success', 'Reason code inactivated successfully!')
    }, err => {
      this.notificationsService.error('Failed', 'Inactive operation is failed!')
    });
  }

  createRequest(type, property, context, key, value): Observable<any> {
    if (property && property.id) {
      return this.apiService.updateOrganizationParameter(
        this.organizationID,
        property.id,
        {
          type: type,
          context: context,
          key: key,
          value: value
        }
      );
    }
    return this.apiService.addOrganizationParameter(this.organizationID, {
      type: type,
      context: context,
      key: key,
      value: value
    });
  }
  
  saveRestrictionCheckbox(context: string) {
    const parameter = {
      type: RESTRICTION_PARAMETERS.type,
      context: RESTRICTION_PARAMETERS.context[context],
      key: RESTRICTION_PARAMETERS.keys[context],
      value: RESTRICTION_PARAMETERS.values[context].active
    };
  
    if (this.additionalFeatures.controls[context].value) {
      this.apiService.addOrganizationParameter(this.organizationID, parameter)
        .subscribe(response => {
          if (response && response.data && this.allRestrictions[context]) {
            this.allRestrictions[context].id = response.data.id;
          } else if (response && response.data) {
            this.allRestrictions[context] = response.data;
          }
          this.notificationsService.success('Success', 'Save operation is successful!');
        }, error => {
          this.notificationsService.error('Failed', 'Save operation is failed!');
        });
    } else if (this.allRestrictions[context]) {
      this.apiService.deleteOrganizationParameters(this.organizationID, this.allRestrictions[context].id)
        .subscribe(response => {
          this.notificationsService.success('Success', 'Save operation is successful!');
        }, error => {
          this.notificationsService.error('Failed', 'Save operation is failed!');
        });
    }
  }
  
  saveSearchPriorityRestriction(context: string) {

    if (this.searchPriorityRestrictionForm.controls.maxSelectedHotels.valid) {
      this.createRequest(
        RESTRICTION_PARAMETERS.type,
        this.searchPriorityRestriction,
        RESTRICTION_PARAMETERS.context[context],
        RESTRICTION_PARAMETERS.keys[context],
        this.searchPriorityRestrictionForm.controls.maxSelectedHotels.value
      ).subscribe(response => {
        if (response && response.data && response.data.instance) {
          this.searchPriorityRestriction = response.data.instance;
        } else {
          this.searchPriorityRestriction = response.data;
        }
        this.notificationsService.success('Success', 'Save operation is successful!');
      }, error => {
        this.notificationsService.error('Failed', 'Save operation is failed!');
      });
    } else {
      this.notificationsService.warn('Warning', 'Please enter number between 1 and 100!');
    }
  }

  updateOfferTypeRestriction(context: string) {
    if (this.rentingRestrictedOfferTypesForm.controls.fullCredit.value || this.rentingRestrictedOfferTypesForm.controls.partialCredit.value) {
      let value = "";
      if (this.rentingRestrictedOfferTypesForm.controls.fullCredit.value) {
        value = "FullCredit"
      }
      if (this.rentingRestrictedOfferTypesForm.controls.partialCredit.value) {
        value = value === "FullCredit" ? "FullCredit|PartialCredit" : "PartialCredit"
      }
      const restrictedOfferTypes = this.tourvisioRentingAgency && this.tourvisioRentingAgency.restrictedOfferTypes ? this.tourvisioRentingAgency.restrictedOfferTypes : null;
      this.createRequest(
        RESTRICTION_PARAMETERS.type,
        restrictedOfferTypes,
        RESTRICTION_PARAMETERS.context[context],
        RESTRICTION_PARAMETERS.keys[context].restrictedOfferTypes,
        value
      ).subscribe(response => {
        if (response && response.data && response.data.instance) {
          this.organizationParameterValue.restrictions.tourvisioRentingAgency = new TourvisioRentingAgencyRestrictions();
          this.organizationParameterValue.restrictions.tourvisioRentingAgency.restrictedOfferTypes = response.data.instance;
          this.tourvisioRentingAgency = this.organizationParameterValue.restrictions.tourvisioRentingAgency;
        } else {
          this.organizationParameterValue.restrictions.tourvisioRentingAgency = new TourvisioRentingAgencyRestrictions();
          this.organizationParameterValue.restrictions.tourvisioRentingAgency.restrictedOfferTypes = response.data;
          this.tourvisioRentingAgency = this.organizationParameterValue.restrictions.tourvisioRentingAgency;
        }
        this.notificationsService.success('Success', 'Save operation is successful!');
      }, error => {
        this.notificationsService.error('Failed', 'Save operation is failed!');
      });
    } else if (!this.rentingRestrictedOfferTypesForm.controls.fullCredit.value 
      && !this.rentingRestrictedOfferTypesForm.controls.partialCredit.value
      && this.tourvisioRentingAgency) {
      this.apiService.deleteOrganizationParameters(this.organizationID, this.tourvisioRentingAgency.restrictedOfferTypes.id)
        .subscribe(response => {
          this.tourvisioRentingAgency = null;
          this.organizationParameterValue.restrictions.tourvisioRentingAgency = null;
          this.notificationsService.success('Success', 'Save operation is successful!');
        }, error => {
          this.notificationsService.error('Failed', 'Save operation is failed!');
        });
    }
  }
  
  deleteSearchPriorityRestriction() {
    if (this.searchPriorityRestriction) {
      this.apiService.deleteOrganizationParameters(this.organizationID, this.searchPriorityRestriction.id)
        .subscribe(response => {
          this.searchPriorityRestrictionForm.controls.maxSelectedHotels.patchValue(null);
          this.searchPriorityRestriction = null;
          this.notificationsService.success('Success', 'Save operation is successful!');
        }, error => {
          this.notificationsService.error('Failed', 'Save operation is failed!');
        });
    } else {
      this.notificationsService.warn('Warning', 'There is no record!');
    }
  }
  

  saveHotelSearchRestrictions(){

    if (!this.hotelSearchRestrictionsForm.controls.currency.valid ||
      !this.hotelSearchRestrictionsForm.controls.country.valid ||
      !this.hotelSearchRestrictionsForm.controls.day.valid) {
      this.notificationsService.warn('Warning', 'Please fill empty fields!')
      return;
    }

    const requests: Array<Observable<any>> = [];

    requests.push(
      this.createRequest(
        RESTRICTION_PARAMETERS.type,
        this.hotelSearchRestrictions ? this.hotelSearchRestrictions.currency : null,
        RESTRICTION_PARAMETERS.context.hotelSearch,
        RESTRICTION_PARAMETERS.keys.hotelSearch.currency,
        this.hotelSearchRestrictionsForm.controls.currency.value
      )
    );

    const countries = this.hotelSearchRestrictionsForm.controls.country.value.map(i => i.countryCode).join('|');
    requests.push(
      this.createRequest(
        RESTRICTION_PARAMETERS.type,
        this.hotelSearchRestrictions ? this.hotelSearchRestrictions.country : null,
        RESTRICTION_PARAMETERS.context.hotelSearch,
        RESTRICTION_PARAMETERS.keys.hotelSearch.country,
        countries
      )
    );

    requests.push(
      this.createRequest(
        RESTRICTION_PARAMETERS.type,
        this.hotelSearchRestrictions ? this.hotelSearchRestrictions.day : null,
        RESTRICTION_PARAMETERS.context.hotelSearch,
        RESTRICTION_PARAMETERS.keys.hotelSearch.day,
        this.hotelSearchRestrictionsForm.controls.day.value
      )
    );

    Observable.forkJoin(requests).subscribe(res => {
      this.notificationsService.success('Success', 'Save operation is successful!');
      this.organizationParameterValue.restrictions = new Restrictions();
      this.organizationParameterValue.restrictions.hotelSearch = new HotelSearchRestrictions();
      res.forEach(item => {
        item = item.data;
        if (item.key == RESTRICTION_PARAMETERS.keys.hotelSearch.currency) {
           this.organizationParameterValue.restrictions.hotelSearch.currency = item;
        }
        if (item.key == RESTRICTION_PARAMETERS.keys.hotelSearch.country) {
          this.organizationParameterValue.restrictions.hotelSearch.country = item;
        }
        if (item.key == RESTRICTION_PARAMETERS.keys.hotelSearch.day) {
          this.organizationParameterValue.restrictions.hotelSearch.day = item;
        }
      })
      this.hotelSearchRestrictions = this.organizationParameterValue.restrictions.hotelSearch;
    }, err => {
      this.notificationsService.error('Failed', 'Save operation is failed!')
    });

  }

  deleteHotelSearchRestrictions(){

    const requests: Array<Observable<any>> = [];

    requests.push(this.apiService.deleteOrganizationParameters(this.organizationID, this.hotelSearchRestrictions.country.id));
    requests.push(this.apiService.deleteOrganizationParameters(this.organizationID, this.hotelSearchRestrictions.currency.id));
    requests.push(this.apiService.deleteOrganizationParameters(this.organizationID, this.hotelSearchRestrictions.day.id));

    Observable.forkJoin(requests).subscribe(res => {
      this.notificationsService.success('Success', 'Save operation is successful!');
      this.organizationParameterValue.restrictions = null;
      this.hotelSearchRestrictions = null;
      this.hotelSearchRestrictionsForm.controls.currency.patchValue(null);
      this.hotelSearchRestrictionsForm.controls.country.patchValue(null);
      this.hotelSearchRestrictionsForm.controls.day.patchValue(null);
    }, err => {
      this.notificationsService.error('Failed', 'Save operation is failed!')
    });
  }

  getHotelSearchRestrictionsCurrency(){
    return this.hotelSearchRestrictions
      ? this.hotelSearchRestrictions.currency.value : null;
  }

  getHotelSearchRestrictionsDay(){
    return this.hotelSearchRestrictions
      ? this.hotelSearchRestrictions.day.value : null;
  }

  getHotelSearchRestrictionsCountries(){
    return this.hotelSearchRestrictions
      ? this.getRestrictedCountries(this.hotelSearchRestrictions.country) : null;
  }

  getRestrictedCountries(hotelSearchRestrictionCountryParameter) {
    const restrictedCountries = [];
    const countryCodes = hotelSearchRestrictionCountryParameter.value.split("|");
    countryCodes.forEach(countryCode => {
      restrictedCountries.push({countryCode})
    })
    return restrictedCountries;
  }

}

export class OrganizationParameterValue {
  currency: OrganizationParameter;
  internationalTimeWindow: OrganizationParameter;
  domesticTimeWindow: OrganizationParameter;
  internationalMaxStops: OrganizationParameter;
  domesticMaxStops: OrganizationParameter;
  travelDeclineReasonCodes: OrganizationParameter[] = [];
  policyReasonCodes: OrganizationParameter[] = [];
  justificationReasonCodes: OrganizationParameter[] = [];
  restrictions: Restrictions;
}



// WEBPACK FOOTER //
// C:/Biziclick/bizisystem/src/app/main/frames/organization-parameter/organization-parameter.component.ts